import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Form,
  Input,
  InputNumber,
  Slider,
  Tabs,
  Statistic,
  Progress,
  Divider,
  Modal,
  Tooltip,
  Empty,
  Spin,
  message,
  theme,
  Popconfirm,
  TimePicker,
  Checkbox,
  Segmented,
  Collapse,
  Alert,
  List,
  Badge,
} from "antd";
import {
  PlusOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  ExperimentOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  SettingOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ApartmentOutlined,
  ReloadOutlined,
  CodeOutlined,
  OrderedListOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  CaretRightOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  SplitCellsOutlined,
  NodeIndexOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";
import { toEditorConfig, hasSocConstraint } from "./strategyFormat";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

// =============================================================================
// CONSTANTS - Rule-based Strategy Configuration
// =============================================================================

// Trigger types for rules
// Note: FCAS dispatch is triggered by AEMO instruction, NOT by price conditions
// FCAS bidding (price-based) is handled separately in the Bidding Centre
const TRIGGER_TYPES = [
  { value: "PRICE", label: "Price-Based", description: "Trigger based on energy market prices (RRP, DNSP)", icon: <DollarOutlined /> },
  { value: "SCHEDULE", label: "Schedule-Based", description: "Trigger at specific times/intervals", icon: <ClockCircleOutlined /> },
  { value: "GROUP_STATE", label: "Group State", description: "Trigger based on control group state", icon: <ApartmentOutlined /> },
  { value: "AEMO_DISPATCH", label: "AEMO FCAS Dispatch", description: "Follow AEMO FCAS dispatch instructions (uses control group's DUID)", icon: <ThunderboltOutlined /> },
];

// Price sources for PRICE trigger (all group/market level prices use $/MWh)
// Note: FCAS prices are NOT here - FCAS bidding is handled separately in Bidding Centre
const PRICE_SOURCES = [
  { value: "SPOT_RRP", label: "Spot RRP", unit: "$/MWh" },
  { value: "DNSP_TARIFF", label: "DNSP Tariff", unit: "$/MWh" },
];

// DNSP Tariff types
const DNSP_TARIFF_TYPES = [
  { value: "AUSGRID_EDITH", label: "Ausgrid EDITH" },
  { value: "SAPN_DYNAMIC", label: "SAPN Dynamic Tariff" },
];

// Price condition operators
const PRICE_OPERATORS = [
  { value: ">", label: "Greater than" },
  { value: ">=", label: "Greater or equal" },
  { value: "<", label: "Less than" },
  { value: "<=", label: "Less or equal" },
  { value: "==", label: "Equal to" },
  { value: "between", label: "Between" },
];

// Action intents
const ACTION_INTENTS = [
  { value: "IDLE", label: "Idle", description: "No active dispatch", color: "default" },
  { value: "CHARGE", label: "Charge", description: "Charge batteries from grid", color: "cyan" },
  { value: "DISCHARGE", label: "Discharge", description: "Discharge batteries to grid", color: "orange" },
  { value: "RESERVE", label: "Reserve", description: "Hold capacity for FCAS bidding", color: "purple" },
  { value: "FCAS_RESPONSE", label: "FCAS Response", description: "Execute AEMO dispatch instruction", color: "blue" },
  { value: "CUSTOM_ALGO", label: "Custom Algorithm", description: "Run custom dispatch algorithm", color: "magenta" },
];

// FCAS market types
const FCAS_MARKETS = [
  { value: "RAISE_1S", label: "Raise 1 Second" },
  { value: "RAISE_6S", label: "Raise 6 Second" },
  { value: "RAISE_60S", label: "Raise 60 Second" },
  { value: "RAISE_5MIN", label: "Raise 5 Minute" },
  { value: "LOWER_1S", label: "Lower 1 Second" },
  { value: "LOWER_6S", label: "Lower 6 Second" },
  { value: "LOWER_60S", label: "Lower 60 Second" },
  { value: "LOWER_5MIN", label: "Lower 5 Minute" },
];

// Stop condition types
const STOP_CONDITION_TYPES = [
  { value: "SOC", label: "State of Charge (%)", description: "Stop when SOC reaches target" },
  { value: "TIME", label: "Time Duration (min)", description: "Stop after duration elapsed" },
  { value: "PRICE", label: "Price Threshold", description: "Stop when price condition no longer met" },
  { value: "ENERGY", label: "Energy Delivered (kWh)", description: "Stop after energy target reached" },
];

// Days of week for schedule triggers
const DAYS_OF_WEEK = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

// Australian timezones
const TIMEZONES = [
  { value: "Australia/Brisbane", label: "Brisbane (AEST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
  { value: "Australia/Melbourne", label: "Melbourne (AEST/AEDT)" },
  { value: "Australia/Adelaide", label: "Adelaide (ACST/ACDT)" },
  { value: "Australia/Perth", label: "Perth (AWST)" },
  { value: "Australia/Darwin", label: "Darwin (ACST)" },
  { value: "Australia/Hobart", label: "Hobart (AEST/AEDT)" },
];

// Evaluation intervals (5-minute aligned with AEMO dispatch intervals)
const EVALUATION_INTERVALS = [
  { value: 5, label: "5 minutes (288 intervals/day)" },
  { value: 15, label: "15 minutes (96 intervals/day)" },
  { value: 30, label: "30 minutes (48 intervals/day)" },
];

// Default rule template
const DEFAULT_RULE = {
  rule_id: "",
  priority: 100,
  enabled: true,
  trigger: {
    type: "PRICE",
    price_source: "SPOT_RRP",
    condition: { operator: ">", value: 100 },
    lookback_intervals: 1,
  },
  action: {
    intent: "DISCHARGE",
    target_power_kw: null,
    constraints: { min_soc_pct: 20, max_soc_pct: 100 },
  },
  timing: {
    min_hold_minutes: 5,
    valid_for_minutes: null,
  },
  enforcement: {
    preemptive: false,
    compliance_lock: false,
    stop_condition: null,
  },
};

// Default strategy JSON structure (for dispatch strategies)
const DEFAULT_STRATEGY_JSON = {
  timezone: "Australia/Brisbane",
  evaluation_interval_minutes: 5,
  rules: [],
};

// =============================================================================
// ALLOCATION STRATEGY CONSTANTS
// =============================================================================

// Allocation distribution methods
const ALLOCATION_METHODS = [
  { value: "PCT_GROUP_COMMAND", label: "% of Group Command", description: "Distribute as percentage of group dispatch command" },
  { value: "PCT_USABLE_CAPACITY", label: "% of Usable Capacity", description: "Distribute based on each system's usable capacity" },
  { value: "PCT_FOOTROOM", label: "% of Footroom", description: "Distribute based on available charging headroom (for charge)" },
  { value: "PCT_HEADROOM", label: "% of Headroom", description: "Distribute based on available discharge headroom (for discharge)" },
  { value: "EQUAL_SPLIT", label: "Equal Split", description: "Distribute equally across all online systems" },
  { value: "PROPORTIONAL_INVERTER", label: "Proportional to Inverter", description: "Distribute proportional to inverter kW capacity" },
];

// Input parameters from group dispatch
const GROUP_DISPATCH_PARAMS = [
  { value: "target_kw", label: "Target kW", description: "Target power from group dispatch" },
  { value: "target_kwh", label: "Target kWh", description: "Target energy from group dispatch" },
  { value: "min_soc_pct", label: "Min SOC %", description: "Minimum SOC constraint from dispatch" },
  { value: "max_soc_pct", label: "Max SOC %", description: "Maximum SOC constraint from dispatch" },
];

// Group parameters available for allocation
const GROUP_PARAMS = [
  { value: "online_systems", label: "Online Systems #", description: "Number of systems currently online" },
  { value: "total_inverter_kw", label: "Total Inverter kW", description: "Sum of all online system inverter capacities" },
  { value: "total_usable_kwh", label: "Total Usable kWh", description: "Sum of all online system usable capacities" },
  { value: "avg_soc_pct", label: "Average SOC %", description: "Average SOC across online systems" },
  { value: "total_headroom_kw", label: "Total Headroom kW", description: "Total discharge headroom available" },
  { value: "total_footroom_kw", label: "Total Footroom kW", description: "Total charge footroom available" },
];

// Default allocation strategy JSON
const DEFAULT_ALLOCATION_JSON = {
  distribution_method: "PCT_USABLE_CAPACITY",
  constraints: {
    min_soc_pct: 10,
    max_soc_pct: 95,
    max_charge_rate_c: 0.5,  // Max charge rate as C-rate
    max_discharge_rate_c: 0.5,  // Max discharge rate as C-rate
    max_charge_kwh_per_system: null,  // Max kWh per system per dispatch
    max_discharge_kwh_per_system: null,
  },
  priority_rules: [
    // Optional: prioritize systems by SOC, health, etc.
  ],
  fallback_behavior: "SKIP",  // SKIP, EQUAL_SPLIT, or PROPORTIONAL
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const generateRuleId = () => {
  return `rule_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 5)}`;
};

const validateStrategyJson = (json) => {
  const errors = [];

  if (!json.timezone) {
    errors.push("Timezone is required");
  }
  if (!json.evaluation_interval_minutes || ![5, 15, 30].includes(json.evaluation_interval_minutes)) {
    errors.push("Evaluation interval must be 5, 15, or 30 minutes");
  }
  if (!Array.isArray(json.rules)) {
    errors.push("Rules must be an array");
  } else {
    json.rules.forEach((rule, idx) => {
      if (!rule.rule_id) {
        errors.push(`Rule ${idx + 1}: rule_id is required`);
      }
      if (!rule.trigger?.type) {
        errors.push(`Rule ${idx + 1}: trigger type is required`);
      }
      if (!rule.action?.intent) {
        errors.push(`Rule ${idx + 1}: action intent is required`);
      }
    });
  }

  return errors;
};

// =============================================================================
// RULE EDITOR COMPONENT
// =============================================================================

function RuleEditor({ rule, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, token, controlGroups = [] }) {
  const [expanded, setExpanded] = useState(true);

  const updateRule = (path, value) => {
    const newRule = JSON.parse(JSON.stringify(rule));
    const keys = path.split(".");
    let obj = newRule;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onChange(newRule);
  };

  // Handle trigger type change - auto-set action and enforcement for AEMO_DISPATCH
  const handleTriggerTypeChange = (triggerType) => {
    const newRule = JSON.parse(JSON.stringify(rule));
    newRule.trigger = newRule.trigger || {};
    newRule.trigger.type = triggerType;

    // When AEMO_DISPATCH is selected, auto-set action and enforcement
    if (triggerType === "AEMO_DISPATCH") {
      newRule.action = newRule.action || {};
      newRule.action.intent = "FCAS_RESPONSE";
      // AEMO dispatch must be preemptive and compliance locked
      newRule.enforcement = newRule.enforcement || {};
      newRule.enforcement.preemptive = true;
      newRule.enforcement.compliance_lock = true;
      newRule.enforcement.stop_condition = null;
    }

    onChange(newRule);
  };

  const isAemoDispatch = rule.trigger?.type === "AEMO_DISPATCH";

  const triggerTypeInfo = TRIGGER_TYPES.find(t => t.value === rule.trigger?.type);
  const actionIntentInfo = ACTION_INTENTS.find(a => a.value === rule.action?.intent);

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderLeft: rule.enabled ? `3px solid ${token.colorPrimary}` : `3px solid ${token.colorTextDisabled}`,
        opacity: rule.enabled ? 1 : 0.7,
      }}
    >
      {/* Rule Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Space>
          <Badge count={rule.priority} style={{ backgroundColor: token.colorPrimary }} />
          <Input
            value={rule.rule_id}
            onChange={(e) => updateRule("rule_id", e.target.value)}
            placeholder="Rule ID"
            style={{ width: 200 }}
            addonBefore="ID"
          />
          <Segmented
            value={rule.enabled ? "enabled" : "disabled"}
            onChange={(v) => updateRule("enabled", v === "enabled")}
            options={[
              { label: "Enabled", value: "enabled" },
              { label: "Disabled", value: "disabled" },
            ]}
            size="small"
          />
        </Space>
        <Space>
          <Tooltip title="Move Up">
            <Button
              type="text"
              icon={<ArrowUpOutlined />}
              onClick={onMoveUp}
              disabled={isFirst}
            />
          </Tooltip>
          <Tooltip title="Move Down">
            <Button
              type="text"
              icon={<ArrowDownOutlined />}
              onClick={onMoveDown}
              disabled={isLast}
            />
          </Tooltip>
          <Tooltip title="Expand/Collapse">
            <Button
              type="text"
              icon={expanded ? <CaretRightOutlined rotate={90} /> : <CaretRightOutlined />}
              onClick={() => setExpanded(!expanded)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this rule?"
            onConfirm={onDelete}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      </div>

      {/* Summary when collapsed */}
      {!expanded && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Tag icon={triggerTypeInfo?.icon}>{triggerTypeInfo?.label}</Tag>
          <Tag color={actionIntentInfo?.color}>{actionIntentInfo?.label}</Tag>
          {rule.timing?.min_hold_minutes && <Tag>Hold: {rule.timing.min_hold_minutes}min</Tag>}
          {rule.enforcement?.preemptive && <Tag color="warning">Preemptive</Tag>}
        </div>
      )}

      {/* Expanded Editor */}
      {expanded && (
        <Collapse
          ghost
          defaultActiveKey={["trigger", "action"]}
          style={{ marginTop: 12 }}
        >
          {/* TRIGGER Section */}
          <Panel
            header={
              <Space>
                <ThunderboltOutlined />
                <Text strong>Trigger</Text>
                <Tag>{triggerTypeInfo?.label}</Tag>
              </Space>
            }
            key="trigger"
          >
            <Row gutter={[16, 12]}>
              <Col span={8}>
                <Form.Item label="Trigger Type" style={{ marginBottom: 0 }}>
                  <Select
                    value={rule.trigger?.type}
                    onChange={handleTriggerTypeChange}
                    options={TRIGGER_TYPES.map(t => ({
                      value: t.value,
                      label: (
                        <Space>
                          {t.icon}
                          {t.label}
                        </Space>
                      ),
                    }))}
                  />
                </Form.Item>
              </Col>

              {rule.trigger?.type === "PRICE" && (
                <>
                  <Col span={8}>
                    <Form.Item label="Price Source" style={{ marginBottom: 0 }}>
                      <Select
                        value={rule.trigger?.price_source}
                        onChange={(v) => updateRule("trigger.price_source", v)}
                        placeholder="Select price source"
                        options={PRICE_SOURCES}
                      />
                    </Form.Item>
                  </Col>
                  {rule.trigger?.price_source === "DNSP_TARIFF" && (
                    <Col span={8}>
                      <Form.Item label="Tariff Type" style={{ marginBottom: 0 }}>
                        <Select
                          value={rule.trigger?.dnsp_tariff_type}
                          onChange={(v) => updateRule("trigger.dnsp_tariff_type", v)}
                          placeholder="Select tariff type"
                          options={DNSP_TARIFF_TYPES}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={8}>
                    <Form.Item label="Condition" style={{ marginBottom: 0 }}>
                      <Select
                        value={rule.trigger?.condition?.operator}
                        onChange={(v) => updateRule("trigger.condition.operator", v)}
                        options={PRICE_OPERATORS}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={`Value (${PRICE_SOURCES.find(p => p.value === rule.trigger?.price_source)?.unit || "$/MWh"})`}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        value={rule.trigger?.condition?.value}
                        onChange={(v) => updateRule("trigger.condition.value", v)}
                        style={{ width: "100%", minHeight: 32 }}
                        placeholder="e.g., 150"
                      />
                    </Form.Item>
                  </Col>
                  {rule.trigger?.condition?.operator === "between" && (
                    <Col span={8}>
                      <Form.Item
                        label={`Max Value (${PRICE_SOURCES.find(p => p.value === rule.trigger?.price_source)?.unit || "$/MWh"})`}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          value={rule.trigger?.condition?.max_value}
                          onChange={(v) => updateRule("trigger.condition.max_value", v)}
                          style={{ width: "100%", minHeight: 32 }}
                          placeholder="e.g., 300"
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={8}>
                    <Form.Item
                      label={
                        <Space>
                          Lookback Intervals
                          <Tooltip title="Number of consecutive 5-min intervals that must meet the price condition before triggering. Prevents false triggers from price spikes.">
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Space>
                      }
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        value={rule.trigger?.lookback_intervals}
                        onChange={(v) => updateRule("trigger.lookback_intervals", v)}
                        min={1}
                        max={12}
                        style={{ width: "100%", minHeight: 32 }}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              {rule.trigger?.type === "SCHEDULE" && (
                <>
                  <Col span={16}>
                    <Form.Item label="Active Days" style={{ marginBottom: 0 }}>
                      <Checkbox.Group
                        value={rule.trigger?.schedule?.days || []}
                        onChange={(v) => updateRule("trigger.schedule.days", v)}
                        options={DAYS_OF_WEEK.map(d => ({ value: d.value, label: d.label }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Start Time" style={{ marginBottom: 0 }}>
                      <TimePicker
                        value={rule.trigger?.schedule?.start_time ? dayjs(rule.trigger.schedule.start_time, "HH:mm") : null}
                        onChange={(v) => updateRule("trigger.schedule.start_time", v?.format("HH:mm"))}
                        format="HH:mm"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="End Time" style={{ marginBottom: 0 }}>
                      <TimePicker
                        value={rule.trigger?.schedule?.end_time ? dayjs(rule.trigger.schedule.end_time, "HH:mm") : null}
                        onChange={(v) => updateRule("trigger.schedule.end_time", v?.format("HH:mm"))}
                        format="HH:mm"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              {rule.trigger?.type === "GROUP_STATE" && (
                <>
                  <Col span={8}>
                    <Form.Item label="State Condition" style={{ marginBottom: 0 }}>
                      <Select
                        value={rule.trigger?.group_state?.condition}
                        onChange={(v) => updateRule("trigger.group_state.condition", v)}
                        options={[
                          { value: "SOC_ABOVE", label: "SOC Above" },
                          { value: "SOC_BELOW", label: "SOC Below" },
                          { value: "CAPACITY_AVAILABLE", label: "Capacity Available" },
                          { value: "SYSTEMS_ONLINE", label: "Systems Online" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Threshold Value" style={{ marginBottom: 0 }}>
                      <InputNumber
                        value={rule.trigger?.group_state?.value}
                        onChange={(v) => updateRule("trigger.group_state.value", v)}
                        style={{ width: "100%", minHeight: 32 }}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              {rule.trigger?.type === "AEMO_DISPATCH" && (
                <Col span={8}>
                  <Form.Item
                    label={
                      <Space>
                        Response Mode
                        <Tooltip title="How to respond to AEMO FCAS dispatch instructions. DUID is automatically taken from the control group.">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Space>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      value={rule.trigger?.aemo_dispatch?.response_mode || "FOLLOW_DISPATCH"}
                      onChange={(v) => updateRule("trigger.aemo_dispatch.response_mode", v)}
                      options={[
                        { value: "FOLLOW_DISPATCH", label: "Follow Dispatch MW" },
                        { value: "MAX_AVAILABLE", label: "Max Available" },
                        { value: "PROPORTIONAL", label: "Proportional" },
                      ]}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Panel>

          {/* ACTION Section */}
          <Panel
            header={
              <Space>
                <PlayCircleOutlined />
                <Text strong>Action</Text>
                <Tag color={actionIntentInfo?.color}>{actionIntentInfo?.label}</Tag>
              </Space>
            }
            key="action"
          >
            <Row gutter={[16, 12]}>
              <Col span={8}>
                <Form.Item label="Intent" style={{ marginBottom: 0 }}>
                  <Select
                    value={rule.action?.intent}
                    onChange={(v) => updateRule("action.intent", v)}
                    options={ACTION_INTENTS.map(a => ({
                      value: a.value,
                      label: (
                        <Space>
                          <Badge color={a.color === "default" ? "gray" : a.color} />
                          {a.label}
                        </Space>
                      ),
                    }))}
                  />
                </Form.Item>
              </Col>

              {/* Hide Target Power and other fields for FCAS_RESPONSE - AEMO dispatch provides these */}
              {rule.action?.intent !== "FCAS_RESPONSE" && (
                <>
                  <Col span={8}>
                    <Form.Item label="Target Power (kW)" style={{ marginBottom: 0 }}>
                      <InputNumber
                        value={rule.action?.target_power_kw}
                        onChange={(v) => {
                          // The engine takes kW over %, so the two are exclusive here.
                          const next = JSON.parse(JSON.stringify(rule));
                          next.action = next.action || {};
                          next.action.target_power_kw = v ?? null;
                          if (v != null) next.action.target_power_pct = null;
                          onChange(next);
                        }}
                        min={0}
                        style={{ width: "100%", minHeight: 32 }}
                        placeholder="Leave both empty for max"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={
                        <Space>
                          Target Power (% of group capacity)
                          <Tooltip title="Percentage of the group's total inverter capacity, recomputed every interval as members come and go. 50 = half power. Leave both power fields empty for max.">
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Space>
                      }
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        value={rule.action?.target_power_pct}
                        onChange={(v) => {
                          const next = JSON.parse(JSON.stringify(rule));
                          next.action = next.action || {};
                          next.action.target_power_pct = v ?? null;
                          if (v != null) next.action.target_power_kw = null;
                          onChange(next);
                        }}
                        min={0}
                        max={100}
                        style={{ width: "100%", minHeight: 32 }}
                        placeholder="e.g., 50"
                      />
                    </Form.Item>
                  </Col>

                  {rule.action?.intent === "CUSTOM_ALGO" && (
                    <Col span={8}>
                      <Form.Item label="Algorithm ID" style={{ marginBottom: 0 }}>
                        <Input
                          value={rule.action?.algorithm_id}
                          onChange={(e) => updateRule("action.algorithm_id", e.target.value)}
                          placeholder="e.g., algo_peak_shave_v2"
                        />
                      </Form.Item>
                    </Col>
                  )}

                  <Col span={24}>
                    <Divider orientation="left" orientationMargin={0} style={{ margin: "8px 0" }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        SOC Constraints{!hasSocConstraint(rule) && " — not set (no SOC gate on this rule; move a slider to add one)"}
                      </Text>
                    </Divider>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Min SOC (%)" style={{ marginBottom: 0 }}>
                      <Slider
                        value={rule.action?.constraints?.min_soc_pct ?? 0}
                        onChange={(v) => updateRule("action.constraints.min_soc_pct", v)}
                        min={0}
                        max={100}
                        marks={{ 0: "0%", 20: "20%", 50: "50%", 100: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Max SOC (%)" style={{ marginBottom: 0 }}>
                      <Slider
                        value={rule.action?.constraints?.max_soc_pct ?? 100}
                        onChange={(v) => updateRule("action.constraints.max_soc_pct", v)}
                        min={0}
                        max={100}
                        marks={{ 0: "0%", 50: "50%", 90: "90%", 100: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
          </Panel>

          {/* TIMING Section */}
          <Panel
            header={
              <Space>
                <ClockCircleOutlined />
                <Text strong>Timing</Text>
                {rule.timing?.min_hold_minutes && <Tag>Hold: {rule.timing.min_hold_minutes}min</Tag>}
              </Space>
            }
            key="timing"
          >
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      Min Hold Duration (minutes)
                      <Tooltip title="Minimum time the action must run before it can be interrupted by lower priority rules">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    value={rule.timing?.min_hold_minutes}
                    onChange={(v) => updateRule("timing.min_hold_minutes", v)}
                    min={0}
                    max={1440}
                    style={{ width: "100%", minHeight: 32 }}
                    placeholder="e.g., 30"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      Valid For (minutes)
                      <Tooltip title="Maximum time this action remains valid. Leave empty for no limit.">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    value={rule.timing?.valid_for_minutes}
                    onChange={(v) => updateRule("timing.valid_for_minutes", v)}
                    min={0}
                    max={1440}
                    style={{ width: "100%", minHeight: 32 }}
                    placeholder="No limit"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Panel>

          {/* ENFORCEMENT Section */}
          <Panel
            header={
              <Space>
                <SettingOutlined />
                <Text strong>Enforcement</Text>
                {rule.enforcement?.preemptive && <Tag color="warning">Preemptive</Tag>}
                {rule.enforcement?.compliance_lock && <Tag color="error">Locked</Tag>}
              </Space>
            }
            key="enforcement"
          >
            <Row gutter={[16, 12]}>
              <Col span={8}>
                <Form.Item
                  label={
                    <Space>
                      Preemptive
                      <Tooltip title={isAemoDispatch ? "AEMO dispatch rules are always preemptive" : "If enabled, this rule can interrupt lower priority rules even during their min_hold period."}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <Segmented
                    value={isAemoDispatch ? "yes" : (rule.enforcement?.preemptive ? "yes" : "no")}
                    onChange={(v) => updateRule("enforcement.preemptive", v === "yes")}
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    size="small"
                    disabled={isAemoDispatch}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={
                    <Space>
                      Compliance Lock
                      <Tooltip title={isAemoDispatch ? "AEMO dispatch rules are always compliance locked" : "If enabled, action CANNOT be interrupted by any other rule once started. Only Emergency Stop can override."}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <Segmented
                    value={isAemoDispatch ? "yes" : (rule.enforcement?.compliance_lock ? "yes" : "no")}
                    onChange={(v) => updateRule("enforcement.compliance_lock", v === "yes")}
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    size="small"
                    disabled={isAemoDispatch}
                  />
                </Form.Item>
              </Col>

              {/* Hide Stop Condition for AEMO Dispatch - dispatch ends when AEMO says so */}
              {!isAemoDispatch && (
                <>
                  <Col span={24}>
                    <Divider orientation="left" orientationMargin={0} style={{ margin: "8px 0" }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Stop Condition</Text>
                    </Divider>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Stop Type" style={{ marginBottom: 0 }}>
                      <Select
                        value={rule.enforcement?.stop_condition?.type}
                        onChange={(v) => {
                          if (v) {
                            updateRule("enforcement.stop_condition", { type: v, value: null });
                          } else {
                            updateRule("enforcement.stop_condition", null);
                          }
                        }}
                        allowClear
                        placeholder="No stop condition"
                        options={STOP_CONDITION_TYPES.map(s => ({
                          value: s.value,
                          label: s.label,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  {rule.enforcement?.stop_condition?.type && (
                    <Col span={8}>
                      <Form.Item label="Stop Value" style={{ marginBottom: 0 }}>
                        <InputNumber
                          value={rule.enforcement?.stop_condition?.value}
                          onChange={(v) => updateRule("enforcement.stop_condition.value", v)}
                          style={{ width: "100%", minHeight: 32 }}
                          placeholder="Target value"
                        />
                      </Form.Item>
                    </Col>
                  )}
                </>
              )}
            </Row>
          </Panel>
        </Collapse>
      )}
    </Card>
  );
}

// =============================================================================
// ALLOCATION STRATEGY EDITOR COMPONENT
// =============================================================================

function AllocationEditor({ config, onChange, token }) {
  const updateConfig = (path, value) => {
    const newConfig = JSON.parse(JSON.stringify(config));
    const keys = path.split(".");
    let obj = newConfig;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onChange(newConfig);
  };

  return (
    <div>
      {/* Distribution Method */}
      <Card
        size="small"
        title={
          <Space>
            <SplitCellsOutlined />
            <Text strong>Distribution Method</Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Form.Item label="How to distribute group dispatch to systems" style={{ marginBottom: 16 }}>
          <Select
            value={config.distribution_method}
            onChange={(v) => updateConfig("distribution_method", v)}
            options={ALLOCATION_METHODS.map(m => ({
              value: m.value,
              label: (
                <div>
                  <div>{m.label}</div>
                  <Text type="secondary" style={{ fontSize: 11 }}>{m.description}</Text>
                </div>
              ),
            }))}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Alert
          type="info"
          showIcon
          message="Distribution Logic"
          description={
            config.distribution_method === "PCT_GROUP_COMMAND" ? "Each system receives a fixed percentage of the group command, regardless of capacity." :
            config.distribution_method === "PCT_USABLE_CAPACITY" ? "Systems with larger usable capacity receive proportionally more of the dispatch." :
            config.distribution_method === "PCT_FOOTROOM" ? "For charging: systems with more available footroom (100% - SOC) receive more." :
            config.distribution_method === "PCT_HEADROOM" ? "For discharging: systems with more available headroom (SOC - min) receive more." :
            config.distribution_method === "EQUAL_SPLIT" ? "Command is split equally across all online systems." :
            "Distribution proportional to each system's inverter kW capacity."
          }
          style={{ marginBottom: 0 }}
        />
      </Card>

      {/* SOC Constraints */}
      <Card
        size="small"
        title={
          <Space>
            <SettingOutlined />
            <Text strong>SOC Constraints</Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Min SOC %" style={{ marginBottom: 8 }}>
              <Slider
                value={config.constraints?.min_soc_pct ?? 10}
                onChange={(v) => updateConfig("constraints.min_soc_pct", v)}
                min={0}
                max={100}
                marks={{ 0: "0%", 10: "10%", 20: "20%", 50: "50%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Max SOC %" style={{ marginBottom: 8 }}>
              <Slider
                value={config.constraints?.max_soc_pct ?? 95}
                onChange={(v) => updateConfig("constraints.max_soc_pct", v)}
                min={0}
                max={100}
                marks={{ 50: "50%", 90: "90%", 95: "95%", 100: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Rate Constraints */}
      <Card
        size="small"
        title={
          <Space>
            <ThunderboltOutlined />
            <Text strong>Rate Constraints</Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <Space>
                  Max Charge Rate (C-rate)
                  <Tooltip title="Maximum charge rate as C-rate (e.g., 0.5 = charge at 50% of battery capacity per hour)">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                value={config.constraints?.max_charge_rate_c ?? 0.5}
                onChange={(v) => updateConfig("constraints.max_charge_rate_c", v)}
                min={0.1}
                max={2}
                step={0.1}
                style={{ width: "100%" }}
                addonAfter="C"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <Space>
                  Max Discharge Rate (C-rate)
                  <Tooltip title="Maximum discharge rate as C-rate">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                value={config.constraints?.max_discharge_rate_c ?? 0.5}
                onChange={(v) => updateConfig("constraints.max_discharge_rate_c", v)}
                min={0.1}
                max={2}
                step={0.1}
                style={{ width: "100%" }}
                addonAfter="C"
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Energy Constraints per System */}
      <Card
        size="small"
        title={
          <Space>
            <NodeIndexOutlined />
            <Text strong>Per-System Energy Limits</Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Max Charge kWh per System"
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                value={config.constraints?.max_charge_kwh_per_system}
                onChange={(v) => updateConfig("constraints.max_charge_kwh_per_system", v)}
                min={0}
                style={{ width: "100%" }}
                placeholder="No limit"
                addonAfter="kWh"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Max Discharge kWh per System"
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                value={config.constraints?.max_discharge_kwh_per_system}
                onChange={(v) => updateConfig("constraints.max_discharge_kwh_per_system", v)}
                min={0}
                style={{ width: "100%" }}
                placeholder="No limit"
                addonAfter="kWh"
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Fallback Behavior */}
      <Card
        size="small"
        title={
          <Space>
            <WarningOutlined />
            <Text strong>Fallback Behavior</Text>
          </Space>
        }
      >
        <Form.Item
          label="If a system cannot accept its allocation"
          style={{ marginBottom: 0 }}
        >
          <Select
            value={config.fallback_behavior || "SKIP"}
            onChange={(v) => updateConfig("fallback_behavior", v)}
            options={[
              { value: "SKIP", label: "Skip - Remaining capacity goes unused" },
              { value: "REDISTRIBUTE", label: "Redistribute - Reallocate to other systems" },
              { value: "PROPORTIONAL", label: "Proportional - Reduce all allocations proportionally" },
            ]}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Card>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function StrategyCentre() {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("strategies");
  const [loading, setLoading] = useState(true);
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [backtestRunning, setBacktestRunning] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [backtestResults, setBacktestResults] = useState(null);
  const [strategyFilter, setStrategyFilter] = useState("all");

  // Rule-based strategy state
  const [strategyJson, setStrategyJson] = useState(DEFAULT_STRATEGY_JSON);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonErrors, setJsonErrors] = useState([]);

  // Strategy type selection
  const [strategyType, setStrategyType] = useState("dispatch"); // 'dispatch' or 'allocation'
  const [typeSelectModalVisible, setTypeSelectModalVisible] = useState(false);

  // Allocation strategy state
  const [allocationConfig, setAllocationConfig] = useState(DEFAULT_ALLOCATION_JSON);

  // Control groups with DUID for AEMO dispatch
  const [controlGroups, setControlGroups] = useState([]);

  // Fetch strategies
  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch("/api/dispatch/strategies");
      const data = await response.json();

      if (data.success) {
        const parsed = (data.data || []).map(s => ({
          ...s,
          config: typeof s.config === "string" ? JSON.parse(s.config) : s.config || {},
          strategyJson: typeof s.strategy_json === "string" ? JSON.parse(s.strategy_json) : s.strategy_json || DEFAULT_STRATEGY_JSON,
        }));
        setStrategies(parsed);
      }
    } catch (error) {
      console.error("Failed to fetch strategies:", error);
      message.error("Failed to load strategies");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch control groups with DUID
  const fetchControlGroups = useCallback(async () => {
    try {
      const response = await authenticatedFetch("/api/control-groups?hasDuid=true");
      const data = await response.json();
      if (data.success) {
        setControlGroups(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch control groups:", error);
    }
  }, []);

  useEffect(() => {
    fetchStrategies();
    fetchControlGroups();
  }, [fetchStrategies, fetchControlGroups]);

  // Add a new rule to the strategy
  const addRule = () => {
    const newRule = {
      ...JSON.parse(JSON.stringify(DEFAULT_RULE)),
      rule_id: generateRuleId(),
      priority: (strategyJson.rules?.length || 0) + 1,
    };
    setStrategyJson({
      ...strategyJson,
      rules: [...(strategyJson.rules || []), newRule],
    });
  };

  // Update a rule
  const updateRule = (index, newRule) => {
    const newRules = [...strategyJson.rules];
    newRules[index] = newRule;
    setStrategyJson({ ...strategyJson, rules: newRules });
  };

  // Delete a rule
  const deleteRule = (index) => {
    const newRules = strategyJson.rules.filter((_, i) => i !== index);
    // Re-assign priorities
    newRules.forEach((r, i) => (r.priority = i + 1));
    setStrategyJson({ ...strategyJson, rules: newRules });
  };

  // Move rule up
  const moveRuleUp = (index) => {
    if (index === 0) return;
    const newRules = [...strategyJson.rules];
    [newRules[index - 1], newRules[index]] = [newRules[index], newRules[index - 1]];
    newRules.forEach((r, i) => (r.priority = i + 1));
    setStrategyJson({ ...strategyJson, rules: newRules });
  };

  // Move rule down
  const moveRuleDown = (index) => {
    if (index === strategyJson.rules.length - 1) return;
    const newRules = [...strategyJson.rules];
    [newRules[index], newRules[index + 1]] = [newRules[index + 1], newRules[index]];
    newRules.forEach((r, i) => (r.priority = i + 1));
    setStrategyJson({ ...strategyJson, rules: newRules });
  };

  // Switch to JSON mode
  const enterJsonMode = () => {
    setJsonText(JSON.stringify(strategyJson, null, 2));
    setJsonMode(true);
    setJsonErrors([]);
  };

  // Apply JSON changes
  const applyJsonChanges = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const errors = validateStrategyJson(parsed);
      if (errors.length > 0) {
        setJsonErrors(errors);
        return;
      }
      setStrategyJson(parsed);
      setJsonMode(false);
      setJsonErrors([]);
      message.success("JSON applied successfully");
    } catch (e) {
      setJsonErrors([`Invalid JSON: ${e.message}`]);
    }
  };

  // Create/Update strategy
  const handleSaveStrategy = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue();

    // Validate based on strategy type
    if (strategyType === "dispatch") {
      const errors = validateStrategyJson(strategyJson);
      if (errors.length > 0) {
        message.error("Strategy JSON has errors: " + errors.join(", "));
        return;
      }
    }

    setSaveLoading(true);
    try {
      // Build config based on strategy type
      const config = strategyType === "dispatch" ? strategyJson : allocationConfig;

      const payload = {
        name: values.name,
        description: values.description,
        strategyType: strategyType,
        config: config,
        isActive: values.isActive,
      };

      const url = editingStrategy
        ? `/api/dispatch/strategies/${editingStrategy.id}`
        : "/api/dispatch/strategies";
      const method = editingStrategy ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        message.success(editingStrategy ? "Strategy updated" : "Strategy created");
        setEditModalVisible(false);
        setEditingStrategy(null);
        form.resetFields();
        setStrategyJson(DEFAULT_STRATEGY_JSON);
        setAllocationConfig(JSON.parse(JSON.stringify(DEFAULT_ALLOCATION_JSON)));
        setStrategyType("dispatch");
        fetchStrategies();
      } else {
        message.error(data.message || "Failed to save strategy");
      }
    } catch (error) {
      console.error("Error saving strategy:", error);
      message.error("Failed to save strategy");
    } finally {
      setSaveLoading(false);
    }
  };

  // Delete strategy
  const handleDeleteStrategy = async (strategyId) => {
    try {
      const response = await authenticatedFetch(`/api/dispatch/strategies/${strategyId}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (data.success) {
        message.success("Strategy deleted");
        fetchStrategies();
      } else {
        message.error(data.message || "Failed to delete strategy");
      }
    } catch (error) {
      message.error("Failed to delete strategy");
    }
  };

  // Clone strategy
  const handleCloneStrategy = async (strategyId) => {
    try {
      const response = await authenticatedFetch(`/api/dispatch/strategies/${strategyId}/clone`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();
      if (data.success) {
        message.success("Strategy cloned successfully");
        fetchStrategies();
      } else {
        message.error(data.message || "Failed to clone strategy");
      }
    } catch (error) {
      message.error("Failed to clone strategy");
    }
  };

  // Open edit modal
  const openEditModal = (strategy = null) => {
    setEditingStrategy(strategy);
    if (strategy) {
      // Editing existing strategy
      form.setFieldsValue({
        name: strategy.name,
        description: strategy.description,
        isActive: strategy.isActive,
      });
      // Determine strategy type from existing data
      const type = strategy.type || strategy.strategy_type || "dispatch";
      setStrategyType(type);

      if (type === "allocation") {
        setAllocationConfig(strategy.config || strategy.strategyJson || DEFAULT_ALLOCATION_JSON);
      } else {
        // Hand-written (engine-format) strategies open with every field mapped
        // onto the editor's; editor-format ones pass through unchanged.
        setStrategyJson(toEditorConfig(strategy.config || strategy.strategyJson || DEFAULT_STRATEGY_JSON));
      }
      setJsonMode(false);
      setJsonErrors([]);
      setEditModalVisible(true);
    } else {
      // Creating new strategy - show type selector first
      form.resetFields();
      form.setFieldsValue({ isActive: false });
      setStrategyJson(JSON.parse(JSON.stringify(DEFAULT_STRATEGY_JSON)));
      setAllocationConfig(JSON.parse(JSON.stringify(DEFAULT_ALLOCATION_JSON)));
      setTypeSelectModalVisible(true);
    }
  };

  // Handle strategy type selection and open edit modal
  const handleTypeSelect = (type) => {
    setStrategyType(type);
    setTypeSelectModalVisible(false);
    setJsonMode(false);
    setJsonErrors([]);
    setEditModalVisible(true);
  };

  // Backtest - no backtest API available yet
  const handleRunBacktest = () => {
    if (!selectedStrategy) {
      message.warning("Please select a strategy first");
      return;
    }

    message.info("Backtesting is not yet available. This feature requires historical market data integration.");
  };

  // Backtest chart data - empty until backtest API is available
  const backtestData = [];

  // Filter strategies
  const filteredStrategies = strategies.filter(s => {
    if (strategyFilter === "all") return true;
    const type = s.type || s.strategy_type || "dispatch";
    if (strategyFilter === "dispatch") return type === "dispatch";
    if (strategyFilter === "allocation") return type === "allocation";
    if (strategyFilter === "active") return s.isActive;
    return true;
  });

  // Strategy table columns
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          {record.description && (
            <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              {record.description}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      key: "type",
      width: 110,
      render: (_, record) => {
        const type = record.type || record.strategy_type || "dispatch";
        return type === "allocation" ? (
          <Tag color="purple" icon={<SplitCellsOutlined />}>Allocation</Tag>
        ) : (
          <Tag color="blue" icon={<ThunderboltOutlined />}>Dispatch</Tag>
        );
      },
    },
    {
      title: "Config",
      key: "config",
      width: 120,
      align: "center",
      render: (_, record) => {
        const type = record.type || record.strategy_type || "dispatch";
        const config = record.config || record.strategyJson;
        if (type === "allocation") {
          const method = ALLOCATION_METHODS.find(m => m.value === config?.distribution_method);
          return <Tag>{method?.label || "Default"}</Tag>;
        } else {
          const ruleCount = config?.rules?.length || 0;
          const enabledCount = config?.rules?.filter(r => r.enabled).length || 0;
          return (
            <Tooltip title={`${enabledCount} enabled / ${ruleCount} total`}>
              <Tag color="blue">{enabledCount} / {ruleCount} rules</Tag>
            </Tooltip>
          );
        }
      },
    },
    {
      title: "Details",
      key: "details",
      width: 150,
      render: (_, record) => {
        const type = record.type || record.strategy_type || "dispatch";
        const config = record.config || record.strategyJson;
        if (type === "allocation") {
          return (
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                SOC: {config?.constraints?.min_soc_pct || 10}% - {config?.constraints?.max_soc_pct || 95}%
              </Text>
            </Space>
          );
        } else {
          const tz = config?.timezone || "Australia/Brisbane";
          const label = TIMEZONES.find(t => t.value === tz)?.label || tz;
          return <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>;
        }
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? "success" : "default"} icon={isActive ? <CheckCircleOutlined /> : <PauseCircleOutlined />}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          <Tooltip title="View JSON">
            <Button
              type="text"
              icon={<CodeOutlined />}
              onClick={() => {
                Modal.info({
                  title: `Strategy JSON: ${record.name}`,
                  width: 700,
                  content: (
                    <pre style={{
                      background: token.colorFillSecondary,
                      padding: 12,
                      borderRadius: 8,
                      maxHeight: 400,
                      overflow: "auto",
                      fontSize: 12,
                    }}>
                      {JSON.stringify(record.strategyJson, null, 2)}
                    </pre>
                  ),
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Clone">
            <Button type="text" icon={<CopyOutlined />} onClick={() => handleCloneStrategy(record.id)} />
          </Tooltip>
          <Tooltip title="Backtest">
            <Button
              type="text"
              icon={<ExperimentOutlined />}
              onClick={() => {
                setSelectedStrategy(record);
                setActiveTab("backtest");
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this strategy?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteStrategy(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Tab items
  const tabItems = [
    {
      key: "strategies",
      label: (
        <Space>
          <OrderedListOutlined />
          Strategies
        </Space>
      ),
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Space>
              <Select
                value={strategyFilter}
                onChange={setStrategyFilter}
                style={{ width: 180 }}
                options={[
                  { value: "all", label: "All Strategies" },
                  { value: "dispatch", label: "Dispatch Strategies" },
                  { value: "allocation", label: "Allocation Strategies" },
                  { value: "active", label: "Active Only" },
                ]}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchStrategies}>
                Refresh
              </Button>
            </Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditModal()}>
              New Strategy
            </Button>
          </div>

          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message="Dispatch & Allocation Strategies"
            description={
              <div>
                <div><strong>Dispatch Strategy:</strong> Group-level decisions (when/what to dispatch) with rule-based triggers and actions.</div>
                <div><strong>Allocation Strategy:</strong> How to distribute group dispatch commands to individual systems.</div>
              </div>
            }
            style={{ marginBottom: 16 }}
          />

          {loading ? (
            <div style={{ textAlign: "center", padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : filteredStrategies.length === 0 ? (
            <Empty description="No strategies found" image={Empty.PRESENTED_IMAGE_SIMPLE}>
              <Button type="primary" onClick={() => openEditModal()}>
                Create First Strategy
              </Button>
            </Empty>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredStrategies}
              rowKey="id"
              pagination={makePagination(pageSize, setPageSize)}
            />
          )}
        </div>
      ),
    },
    {
      key: "backtest",
      label: (
        <Space>
          <BarChartOutlined />
          Backtest
        </Space>
      ),
      children: (
        <div>
          <Card bordered={false} style={{ borderRadius: 12, marginBottom: 24 }}>
            <Row gutter={24} align="middle">
              <Col xs={24} md={10}>
                <Form.Item label="Select Strategy" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="Choose a strategy to backtest"
                    value={selectedStrategy?.id}
                    onChange={(v) => setSelectedStrategy(strategies.find((s) => s.id === v))}
                    options={strategies.map((s) => ({
                      value: s.id,
                      label: `${s.name} (${s.strategyJson?.rules?.length || 0} rules)`,
                    }))}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Period" style={{ marginBottom: 0 }}>
                  <Select
                    defaultValue="30d"
                    options={[
                      { value: "7d", label: "Last 7 Days" },
                      { value: "30d", label: "Last 30 Days" },
                      { value: "90d", label: "Last 90 Days" },
                    ]}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <div style={{ display: "flex", alignItems: "flex-end", height: 54 }}>
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    loading={backtestRunning}
                    onClick={handleRunBacktest}
                    size="large"
                  >
                    {backtestRunning ? "Running..." : "Run Backtest"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>

          {backtestResults && (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={8} md={4}>
                  <Card bordered={false} style={{ borderRadius: 12 }}>
                    <Statistic
                      title="Total Revenue"
                      value={backtestResults.totalRevenue}
                      prefix="$"
                      valueStyle={{ color: token.colorSuccess }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card bordered={false} style={{ borderRadius: 12 }}>
                    <Statistic title="Avg Daily" value={backtestResults.avgDailyRevenue} prefix="$" />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card bordered={false} style={{ borderRadius: 12 }}>
                    <Statistic title="Dispatches" value={backtestResults.totalDispatches} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card bordered={false} style={{ borderRadius: 12 }}>
                    <Statistic title="Rules Triggered" value={backtestResults.rulesTriggered} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card bordered={false} style={{ borderRadius: 12 }}>
                    <Statistic
                      title="Success Rate"
                      value={backtestResults.successRate}
                      suffix="%"
                      valueStyle={{ color: token.colorSuccess }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card bordered={false} style={{ borderRadius: 12 }}>
                    <Statistic
                      title="vs Baseline"
                      value={backtestResults.improvement}
                      prefix="+"
                      suffix="%"
                      valueStyle={{ color: token.colorSuccess }}
                    />
                  </Card>
                </Col>
              </Row>

              <Card bordered={false} style={{ borderRadius: 12 }} title="Backtest Results - Strategy vs Baseline">
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={backtestData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                      <defs>
                        <linearGradient id="strategyGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={token.colorSuccess} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={token.colorSuccess} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="baselineGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={token.colorWarning} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={token.colorWarning} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                      <RechartsTooltip formatter={(v) => [`$${v}`, ""]} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        name="Strategy"
                        stroke={token.colorSuccess}
                        fill="url(#strategyGrad)"
                      />
                      <Area
                        type="monotone"
                        dataKey="baseline"
                        name="Baseline"
                        stroke={token.colorWarning}
                        fill="url(#baselineGrad)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </>
          )}

          {!backtestResults && !backtestRunning && (
            <Card bordered={false} style={{ borderRadius: 12, textAlign: "center", padding: 48 }}>
              <ExperimentOutlined style={{ fontSize: 48, color: token.colorTextSecondary, marginBottom: 16 }} />
              <Title level={4} type="secondary">
                Select a strategy to run a backtest
              </Title>
              <Paragraph type="secondary">
                Backtesting simulates how your strategy would have performed using historical market data
              </Paragraph>
            </Card>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageLayout active="strategyCenter">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Strategy Centre
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Build and configure rule-based dispatch strategies with triggers, actions, timing, and enforcement
        </Text>
      </div>

      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* Strategy Type Selector Modal */}
      <Modal
        title="Select Strategy Type"
        open={typeSelectModalVisible}
        onCancel={() => setTypeSelectModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ padding: "16px 0" }}>
          <Row gutter={16}>
            <Col span={12}>
              <Card
                hoverable
                style={{ textAlign: "center", cursor: "pointer" }}
                onClick={() => handleTypeSelect("dispatch")}
              >
                <ThunderboltOutlined style={{ fontSize: 48, color: token.colorPrimary, marginBottom: 16 }} />
                <Title level={4} style={{ margin: 0 }}>Dispatch Strategy</Title>
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                  Group-level dispatch decisions. Define triggers (price, schedule, AEMO) and actions (charge, discharge, reserve).
                </Paragraph>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                hoverable
                style={{ textAlign: "center", cursor: "pointer" }}
                onClick={() => handleTypeSelect("allocation")}
              >
                <SplitCellsOutlined style={{ fontSize: 48, color: "#f5a623", marginBottom: 16 }} />
                <Title level={4} style={{ margin: 0 }}>Allocation Strategy</Title>
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                  System-level distribution rules. Define how group dispatch commands are allocated to individual systems.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>
      </Modal>

      {/* Strategy Edit/Create Modal */}
      <Modal
        title={
          <Space>
            {editingStrategy ? <EditOutlined /> : <PlusOutlined />}
            {editingStrategy ? "Edit Strategy" : "Create New Strategy"}
            {strategyType === "allocation" ? (
              <Tag color="purple" icon={<SplitCellsOutlined />}>Allocation</Tag>
            ) : (
              <Tag color="blue" icon={<ThunderboltOutlined />}>Dispatch</Tag>
            )}
          </Space>
        }
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingStrategy(null);
          form.resetFields();
          setStrategyJson(DEFAULT_STRATEGY_JSON);
          setAllocationConfig(JSON.parse(JSON.stringify(DEFAULT_ALLOCATION_JSON)));
          setStrategyType("dispatch");
          setJsonMode(false);
        }}
        footer={null}
        width={950}
        destroyOnClose
        styles={{ body: { maxHeight: "calc(100vh - 200px)", overflowY: "auto" } }}
      >
        <Form form={form} layout="vertical">
          {/* Basic Info */}
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="name" label="Strategy Name" rules={[{ required: true, message: "Name is required" }]}>
                <Input placeholder={strategyType === "allocation" ? "e.g., Proportional Capacity Distribution" : "e.g., Peak Price Arbitrage"} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isActive"
                label="Status"
                getValueProps={(value) => ({ value: value ? "active" : "inactive" })}
                getValueFromEvent={(v) => v === "active"}
              >
                <Segmented
                  options={[
                    { label: "Active", value: "active" },
                    { label: "Inactive", value: "inactive" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder={strategyType === "allocation" ? "Describe how this strategy distributes commands to systems" : "Describe what this strategy does"} />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>
            {strategyType === "allocation" ? "Allocation Configuration" : "Dispatch Configuration"}
          </Divider>

          {/* Allocation Strategy Configuration */}
          {strategyType === "allocation" ? (
            <AllocationEditor
              config={allocationConfig}
              onChange={setAllocationConfig}
              token={token}
            />
          ) : (
            <>
              {/* Dispatch Strategy Global Settings */}
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                  <Form.Item label="Timezone" style={{ marginBottom: 0 }}>
                    <Select
                      value={strategyJson.timezone}
                      onChange={(v) => setStrategyJson({ ...strategyJson, timezone: v })}
                      options={TIMEZONES}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Evaluation Interval" style={{ marginBottom: 0 }}>
                    <Select
                      value={strategyJson.evaluation_interval_minutes}
                      onChange={(v) => setStrategyJson({ ...strategyJson, evaluation_interval_minutes: v })}
                      options={EVALUATION_INTERVALS}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <div style={{ display: "flex", alignItems: "flex-end", height: "100%" }}>
                    <Button
                      icon={<CodeOutlined />}
                      onClick={jsonMode ? () => setJsonMode(false) : enterJsonMode}
                      type={jsonMode ? "primary" : "default"}
                    >
                      {jsonMode ? "Visual Editor" : "JSON Editor"}
                    </Button>
                  </div>
                </Col>
              </Row>

          {/* JSON Mode */}
          {jsonMode ? (
            <div>
              {jsonErrors.length > 0 && (
                <Alert
                  type="error"
                  message="JSON Validation Errors"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {jsonErrors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  }
                  style={{ marginBottom: 12 }}
                />
              )}
              <TextArea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                rows={20}
                style={{ fontFamily: "monospace", fontSize: 12 }}
              />
              <div style={{ marginTop: 12, textAlign: "right" }}>
                <Space>
                  <Button onClick={() => setJsonMode(false)}>Cancel</Button>
                  <Button type="primary" onClick={applyJsonChanges}>
                    Apply JSON
                  </Button>
                </Space>
              </div>
            </div>
          ) : (
            <>
              {/* Visual Rule Editor */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <Text strong>Rules ({strategyJson.rules?.length || 0})</Text>
                  <Button type="dashed" icon={<PlusOutlined />} onClick={addRule}>
                    Add Rule
                  </Button>
                </div>

                {strategyJson.rules?.length === 0 && (
                  <Empty
                    description="No rules defined"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: 24, background: token.colorFillSecondary, borderRadius: 8 }}
                  >
                    <Button type="primary" onClick={addRule}>
                      Add First Rule
                    </Button>
                  </Empty>
                )}

                {strategyJson.rules?.map((rule, index) => (
                  <RuleEditor
                    key={rule.rule_id || index}
                    rule={rule}
                    onChange={(newRule) => updateRule(index, newRule)}
                    onDelete={() => deleteRule(index)}
                    onMoveUp={() => moveRuleUp(index)}
                    onMoveDown={() => moveRuleDown(index)}
                    isFirst={index === 0}
                    isLast={index === strategyJson.rules.length - 1}
                    token={token}
                    controlGroups={controlGroups}
                  />
                ))}
              </div>

            </>
          )}
            </>
          )}

          {/* Save Actions - shown for both strategy types */}
          <div style={{ textAlign: "right", marginTop: 24, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 16 }}>
            <Space>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingStrategy(null);
                  form.resetFields();
                  setStrategyJson(DEFAULT_STRATEGY_JSON);
                  setAllocationConfig(JSON.parse(JSON.stringify(DEFAULT_ALLOCATION_JSON)));
                  setStrategyType("dispatch");
                }}
              >
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saveLoading} onClick={handleSaveStrategy}>
                {editingStrategy ? "Update Strategy" : "Create Strategy"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </PageLayout>
  );
}
